import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { classAPI } from '../utils/api';
import useDebouncedValue from '../hooks/useDebouncedValue';
import '../styles/list.css';
import '../styles/admins.css';

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;

const initialForm = {
  name: '',
  isEnabled: true,
};

const Classes = () => {
  const [classes, setClasses] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedClass, setSelectedClass] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const normalizedSearch = debouncedSearch.trim().toLowerCase();
  const isSearchReady = !normalizedSearch || normalizedSearch.length >= MIN_SEARCH_LENGTH;

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    setLoading(true);
    try {
      const response = await classAPI.getAll({ includeDisabled: 1 });
      setClasses(response.data.data.classes || []);
    } catch (fetchError) {
      setError(fetchError.response?.data?.message || 'Failed to load classes');
    } finally {
      setLoading(false);
    }
  };

  const filteredClasses = classes.filter((item) => {
    if (!isSearchReady || !normalizedSearch) {
      return true;
    }

    return item.name.toLowerCase().includes(normalizedSearch);
  });

  const openCreateModal = () => {
    setModalMode('create');
    setSelectedClass(null);
    setFormData(initialForm);
    setError('');
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setModalMode('edit');
    setSelectedClass(item);
    setFormData({
      name: item.name,
      isEnabled: Number(item.isEnabled) === 1,
    });
    setError('');
    setShowModal(true);
  };

  const closeModal = (force = false) => {
    if (saving && !force) return;
    setShowModal(false);
    setSelectedClass(null);
    setFormData(initialForm);
    setError('');
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.name.trim()) {
      setError('Class name is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        isEnabled: formData.isEnabled,
      };

      if (modalMode === 'edit' && selectedClass) {
        await classAPI.update(selectedClass.id, payload);
        setSuccess('Class updated successfully');
      } else {
        await classAPI.create(payload);
        setSuccess('Class created successfully');
      }

      closeModal(true);
      fetchClasses();
    } catch (submitError) {
      setError(submitError.response?.data?.message || 'Failed to save class');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="list-page">
        <div className="list-header">
          <div>
            <h1>Manage Classes</h1>
            <p className="admin-page-subtitle">Super admins can create, edit, and enable or disable classes for student add/edit.</p>
          </div>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <i className="fas fa-plus"></i> Add Class
          </button>
        </div>

        {success && <div className="alert alert-success">{success}</div>}
        {error && !showModal && <div className="alert alert-error">{error}</div>}

        <div className="card">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Type at least 3 characters to search by class name..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <i className="fas fa-magnifying-glass"></i>
          </div>

          {!isSearchReady && (
            <div className="alert alert-info">Type at least 3 characters to search.</div>
          )}

          {loading ? (
            <div className="spinner"></div>
          ) : filteredClasses.length > 0 ? (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Class Name</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClasses.map((item) => {
                    const isEnabled = Number(item.isEnabled) === 1;

                    return (
                      <tr key={item.id}>
                        <td>{item.name}</td>
                        <td>
                          <span className={`badge ${isEnabled ? 'badge-success' : 'badge-warning'}`}>
                            {isEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </td>
                        <td>{item.created_at ? new Date(item.created_at).toLocaleDateString('en-IN') : '-'}</td>
                        <td className='width-100'>
                          <div className="action-buttons">
                            <button className="btn btn-secondary" onClick={() => openEditModal(item)}>
                              <i className="fas fa-pen"></i> Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="alert alert-info">No classes found.</div>
          )}
        </div>

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content admin-modal" onClick={(event) => event.stopPropagation()}>
              <div className="modal-header">
                <h2>{modalMode === 'edit' ? 'Edit Class' : 'Create Class'}</h2>
                <button className="modal-close" onClick={closeModal} type="button">
                  x
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {error && <div className="alert alert-error">{error}</div>}

                  <div className="form-group">
                    <label>Class Name*</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter class name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="isEnabled"
                        checked={formData.isEnabled}
                        onChange={handleChange}
                        className="me-2"
                      />
                      Enable this class for add and edit student
                    </label>
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={saving}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : modalMode === 'edit' ? 'Update Class' : 'Create Class'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Classes;
